import { Components, Theme } from '@mui/material'
import { DISABLED_OPACITY } from './disabledOpacity'

// Scoped to the small variant so the two medium sliders already in the app (risks, suppliers) keep
// MUI's defaults. A stop's own disabled state can't travel through slotProps — Slider resolves the
// mark and markLabel slot props once for every mark — so the call site tags each stop's label node
// with `data-stop-disabled`, and both appearances are decided here.
export const MuiSlider: Components<Omit<Theme, 'components'>>['MuiSlider'] = {
  variants: [
    {
      props: { size: 'small' },
      style: ({ theme }) => ({
        height: 4,
        // The rail runs past the first and last stops by this inset, so the stops (and the labels
        // centred on them) sit inside the slider's box. MUI places stops across the root's full
        // width, so the root is narrowed by the inset and the rail and track reach back out over it.
        display: 'block',
        width: 'auto',
        marginInline: theme.spacing(3),
        // The label row hangs below the tick row, further down than MUI's own marked margin allows for.
        marginBottom: theme.spacing(8),
        '& .MuiSlider-rail': {
          backgroundColor: theme.palette.bg.neutral.muted,
          opacity: 1,
          left: `-${theme.spacing(3)}`,
          width: `calc(100% + 2 * ${theme.spacing(3)})`,
        },
        // The track's left and width are inline styles, so it reaches the rail's start through its
        // own padding rather than by moving its left edge.
        '& .MuiSlider-track': {
          boxSizing: 'content-box',
          marginLeft: `-${theme.spacing(3)}`,
          paddingLeft: theme.spacing(3),
        },
        '& .MuiSlider-thumb': {
          width: 16,
          height: 16,
          backgroundColor: theme.palette.bg.surface.default,
          // currentColor, like the track, so a disabled slider greys the ring with it.
          border: '4px solid currentColor',
        },
        // Offsets are from the rail's centre, so they hold when a coarse pointer enlarges the padding.
        '& .MuiSlider-mark': {
          top: 'calc(50% + 14px)',
          transform: 'translateX(-50%)',
          backgroundColor: theme.palette.border.strong,
          width: theme.strokeWidth.strong,
          height: 6,
          borderRadius: 0,
          // Ladder stops are peers, not progress, so the ones the track has passed keep the same tick.
          '&.MuiSlider-markActive': {
            backgroundColor: theme.palette.border.strong,
            opacity: 1,
          },
          // An unlabelled stop gets a short tick, so the labelled landmarks stand out.
          '&:has(+ .MuiSlider-markLabel > :empty)': {
            height: 3,
          },
          // A tick is the sibling immediately before its own label, which is the only element the
          // call site can tag; :has reaches forward to it. Its specificity beats the markActive rule
          // above, which matters because the unavailable stops are exactly the ones the track passes.
          '&:has(+ .MuiSlider-markLabel [data-stop-disabled])': {
            opacity: DISABLED_OPACITY,
          },
        },
        '& .MuiSlider-markLabel': {
          ...theme.typography['small-regular'],
          top: 'calc(50% + 28px)',
          color: theme.palette.text.muted,
          '& [data-stop-disabled]': {
            opacity: DISABLED_OPACITY,
          },
        },
        '& .MuiSlider-thumb.Mui-focusVisible': {
          boxShadow: theme.customShadows.activeBrand,
        },
      }),
    },
  ],
}
